import { Box, Grid, TextField } from '@mui/material';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { FloatingButton } from '../../../../Buttons';
import { useDispatch } from 'react-redux';
import { setBrand, toggleAlert } from '../../../../../redux/actions';
import { errorMessageParser } from '../../../../../utils/helpers/ToastHelper';
import { Brand, ChangeEventType } from '../../../../../types';
import { DiySettings } from '../../../../../types/IDiy';
import { useForm } from 'react-hook-form';
import { createOrUpdateDiySettings } from '../../../../../services/diy';
import {
  securedUrlPattern,
  validateEmailList,
} from '../../../../../utils/validation';
import { editBrand } from '../../../../../services/brand';

interface BusinessAssetzProps {
  brand: Brand;
  diySettings: DiySettings;
  setDiySettings: Dispatch<SetStateAction<DiySettings>>;
}

const BusinessAssetz: FC<BusinessAssetzProps> = ({
  brand,
  diySettings,
  setDiySettings,
}) => {
  const dispatch = useDispatch();
  const {
    register,
    clearErrors,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({ mode: 'onSubmit' });

  const [loading, setLoading] = useState<boolean>(false);
  const [diySettingsForm, setDiySettingsForm] =
    useState<DiySettings>(diySettings);
  const [brandForm, setBrandForm] = useState<Brand>({ ...brand });

  useEffect(() => {
    if (diySettingsForm?.defaultEmailRecipients?.length > 0) {
      setValue(
        'defaultEmailRecipients',
        (diySettingsForm?.defaultEmailRecipients || [])?.join(','),
      );
    }
  }, [diySettingsForm?.defaultEmailRecipients]);

  const handleOnSubmit = async () => {
    try {
      setLoading(true);

      const updatedBrand = await editBrand(brandForm, brand?._id);

      const diySettings = await createOrUpdateDiySettings(
        brand?._id,
        diySettingsForm,
      );

      setDiySettings(diySettings.data);

      dispatch(setBrand(updatedBrand.updatedBrand));

      dispatch(
        toggleAlert({
          toggle: true,
          message: 'Business assetz updated successfully',
        }),
      );
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);
      dispatch(
        toggleAlert({
          toggle: true,
          menubar: true,
          message: errorMsg,
          type: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOnChangeDiySettings = (e: ChangeEventType) => {
    const name = e.target.name;
    clearErrors(name);

    let value: any = e.target.value;

    if (name === 'defaultEmailRecipients') {
      value = e.target.value.split(',');
    }

    setDiySettingsForm({
      ...diySettingsForm,
      [name]: value,
    });
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                {...register('defaultEmailRecipients', {
                  required: true,
                  validate: validateEmailList,
                })}
                fullWidth
                variant="standard"
                type="text"
                name="defaultEmailRecipients"
                label="Default Email Recipients"
                onChange={handleOnChangeDiySettings}
                value={
                  (diySettingsForm?.defaultEmailRecipients || [])?.join(',') ||
                  ''
                }
                InputLabelProps={{ shrink: true }}
                size="small"
                multiline
                error={!!errors.defaultEmailRecipients}
                helperText={
                  errors?.defaultEmailRecipients?.message ||
                  (errors?.defaultEmailRecipients &&
                    'Please enter at least 1 email recipient') ||
                  'Enter list of email addresses separated by comma'
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="standard"
                type="text"
                id="phone"
                name="phoneNumber"
                label="Phone Number"
                onChange={(e: ChangeEventType) => {
                  setBrandForm({
                    ...brandForm,
                    [e.target.name]: e.target.value,
                  });
                }}
                value={brandForm?.phoneNumber}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                {...register('defaultUrl', {
                  required: true,
                  pattern: securedUrlPattern,
                })}
                fullWidth
                required
                variant="standard"
                type="text"
                id="defaultUrl"
                name="defaultUrl"
                label="Default Website URL"
                onChange={handleOnChangeDiySettings}
                value={diySettingsForm?.defaultUrl}
                InputLabelProps={{ shrink: true }}
                size="small"
                error={!!errors.defaultUrl}
                helperText={
                  errors?.defaultUrl?.message ||
                  (errors?.defaultUrl && 'Default website URL is required') ||
                  'Enter list of email addresses separated by comma'
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                {...register('defaultCalendarUrl', {
                  pattern: securedUrlPattern,
                })}
                fullWidth
                variant="standard"
                type="text"
                id="defaultCalendarUrl"
                name="defaultCalendarUrl"
                label="Default Calendar URL"
                onChange={handleOnChangeDiySettings}
                value={diySettingsForm?.defaultCalendarUrl}
                InputLabelProps={{ shrink: true }}
                size="small"
                error={!!errors.defaultCalendarUrl}
                helperText={errors?.defaultCalendarUrl?.message || ''}
              />
            </Grid>

            {brand?.__type === 'brand' ? (
              <Grid item xs={12}>
                <TextField
                  {...register('defaultPrivacyPolicyUrl', {
                    pattern: securedUrlPattern,
                  })}
                  fullWidth
                  variant="standard"
                  type="text"
                  id="defaultPrivacyPolicyUrl"
                  name="defaultPrivacyPolicyUrl"
                  label="Default Privacy Policy URL"
                  onChange={handleOnChangeDiySettings}
                  value={diySettingsForm?.defaultPrivacyPolicyUrl}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  error={!!errors.defaultPrivacyPolicyUrl}
                  helperText={errors?.defaultPrivacyPolicyUrl?.message || ''}
                />
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      </Grid>

      <Box sx={{ paddingTop: '20px' }}>
        <FloatingButton
          title="Save"
          onClick={handleSubmit(handleOnSubmit)}
          loading={loading}
          bottom="60px"
          fixed
        />
      </Box>
    </Box>
  );
};

export default BusinessAssetz;
